import type { Asset } from "rujira.js";
import type { V2Coin } from "../../redacted/v2/action";

export type FeeAssetMetadata = Pick<Asset, "metadata" | "variants">;

export const privateFeeMetadata = (
  denom: string,
  assets: readonly FeeAssetMetadata[]
) => {
  const metadata =
    denom === "rune"
      ? { symbol: "RUNE", decimals: 8 }
      : assets.find((asset) => asset.variants?.native?.denom === denom)
          ?.metadata;
  return metadata &&
    Number.isInteger(metadata.decimals) &&
    metadata.decimals >= 0 &&
    metadata.decimals <= 30 &&
    metadata.symbol
    ? metadata
    : undefined;
};

const decimalSeparator = (locale?: string) =>
  new Intl.NumberFormat(locale)
    .formatToParts(1.1)
    .find((part) => part.type === "decimal")?.value ?? ".";

/** Add exact quoted privacy charges without combining different assets. */
export const combinedPrivateFees = (
  protocolFees: readonly V2Coin[],
  relayerFee?: V2Coin
): V2Coin[] => {
  const byDenom = new Map<string, bigint>();
  for (const coin of [...protocolFees, ...(relayerFee ? [relayerFee] : [])]) {
    if (!coin.denom || !/^\d+$/.test(coin.amount))
      throw new Error("Invalid private fee amount");
    byDenom.set(
      coin.denom,
      (byDenom.get(coin.denom) ?? 0n) + BigInt(coin.amount)
    );
  }
  return [...byDenom]
    .filter(([, amount]) => amount > 0n)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([denom, amount]) => ({ denom, amount: amount.toString() }));
};

/** Exact decimal conversion: never round a nonzero fee to zero or infer a
 * symbol/precision by splitting a contract denomination. */
export const privateFeeAmount = (
  coin: V2Coin,
  assets: readonly FeeAssetMetadata[],
  locale?: string
): string => {
  if (!/^\d+$/.test(coin.amount)) throw new Error("Invalid private fee amount");
  const amount = BigInt(coin.amount);
  const metadata = privateFeeMetadata(coin.denom, assets);
  if (!metadata) {
    return `${amount.toLocaleString(locale)} base units (${coin.denom})`;
  }
  const scale = 10n ** BigInt(metadata.decimals);
  const whole = (amount / scale).toLocaleString(locale);
  const fraction = (amount % scale)
    .toString()
    .padStart(metadata.decimals, "0")
    .replace(/0+$/, "");
  return `${whole}${fraction ? decimalSeparator(locale) + fraction : ""} ${metadata.symbol}`;
};

/** Match the market fee's compact digits/zero-count style without converting
 * base units to floating point or losing tiny high-decimal token amounts. */
export const privateFeeDisplay = (
  coin: V2Coin,
  assets: readonly FeeAssetMetadata[],
  locale?: string
): { compact: string; exact: string; approximate: boolean } => {
  const exact = privateFeeAmount(coin, assets, locale);
  const metadata = privateFeeMetadata(coin.denom, assets);
  if (!metadata || BigInt(coin.amount) === 0n)
    return { compact: exact, exact, approximate: false };

  const amount = BigInt(coin.amount);
  const integerDigits = amount.toString().length - metadata.decimals;
  const places = Math.min(
    metadata.decimals,
    integerDigits >= 3 ? 2 : integerDigits > 0 ? 3 : 3 - integerDigits
  );
  const discardedScale = 10n ** BigInt(metadata.decimals - places);
  const approximate = amount % discardedScale !== 0n;
  const displayAmount = (amount / discardedScale) * discardedScale;
  const scale = 10n ** BigInt(metadata.decimals);
  const whole = displayAmount / scale;
  let fraction = (displayAmount % scale)
    .toString()
    .padStart(metadata.decimals, "0")
    .slice(0, places)
    .replace(/0+$/, "");

  const zeros = fraction.match(/^0+/)?.[0].length ?? 0;
  if (whole === 0n && zeros >= 3) {
    const subscript = zeros
      .toString()
      .split("")
      .map((digit) => "₀₁₂₃₄₅₆₇₈₉"[Number(digit)])
      .join("");
    fraction = `0${subscript}${fraction.slice(zeros)}`;
  }
  const compact = `${approximate ? "≈" : ""}${whole.toLocaleString(locale)}${fraction ? decimalSeparator(locale) + fraction : ""} ${metadata.symbol}`;
  return { compact, exact, approximate };
};

/** Separate the verified asset label for styling without changing any digits.
 * Unknown denominations keep the complete, explicit base-unit fallback. */
export const privateFeeDisplayParts = (
  coin: V2Coin,
  assets: readonly FeeAssetMetadata[],
  locale?: string
): ReturnType<typeof privateFeeDisplay> & {
  amount: string;
  symbol?: string;
} => {
  const display = privateFeeDisplay(coin, assets, locale);
  const metadata = privateFeeMetadata(coin.denom, assets);
  if (!metadata) return { ...display, amount: display.compact };
  // The formatter appends this exact metadata symbol, including any spaces.
  // Do not split localized numbers or infer a symbol from the denomination.
  return {
    ...display,
    amount: display.compact.slice(0, -(metadata.symbol.length + 1)),
    symbol: metadata.symbol,
  };
};
